import * as Portal from '@radix-ui/react-portal'
import { observer } from 'mobx-react-lite'
import React, {
  createContext,
  CSSProperties,
  Dispatch,
  FC,
  MutableRefObject,
  SetStateAction,
  useContext
} from 'react'
import { GRAY_COLORS, styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { ILoopRefStore } from '../../../stores/song/loop/LoopRefStore'
import { Box, Image, Row } from '../../core/Components'
import { gray4 } from '../../platform/color'
import ContextMenu from '../controls/ContextMenu'
import EmptyInstrumentLoop from './EmptyInstrumentLoop'
import LoopContextMenu from './LoopContextMenu'
import LoopNoteVisualizer from './LoopNoteVisualizer'
import SelectLoopDropdown from './SelectLoopDropdown'
import useInstrumentLoop from './useInstrumentLoop'

interface LoopContextType {
  trackHeight: string
  loopColor: string
  loopNumber: number
  stemIndex: number
  section: number
  instrumentLoopWidthPX: number
  instrumentLoopStyling: {}
  showLoopDropdown: boolean
  setShowLoopDropdown: Dispatch<SetStateAction<boolean>>
  isHoveringDropdown: boolean
  setIsHoveringDropdown: Dispatch<SetStateAction<boolean>>
  letterHeight: string
  isSelected: boolean
  isExactSelected: boolean
  setIsHover: Dispatch<SetStateAction<boolean>>
  isHover: boolean
  removeLoop: () => void
  styles: { [key: string]: CSSProperties }
  attributes: {
    [key: string]: {
      [key: string]: string
    }
  }
  setTooltip: Dispatch<SetStateAction<HTMLDivElement>>
  setTooltipBase: Dispatch<SetStateAction<HTMLDivElement>>
  currLoop: ILoopRefStore
  drag: any
  connectDrop: any
}
export const LoopContext = createContext<LoopContextType>(null)

export const LOOP_LETTERS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z'
]

const Container = styled('div', {
  padding: '1px',
  paddingRight: '2px',
  paddingBottom: '1px',
  boxSizing: 'border-box',
  borderBottom: `1px solid ${gray4}`,
  background: GRAY_COLORS.gray005,
  variants: {
    even: {
      true: {
        background: GRAY_COLORS.gray01
      }
    }
  }
})

const LoopContainer = styled('div', {
  position: 'relative',
  background: 'transparent',
  boxSizing: 'border-box',
  borderRadius: '6px',
  color: 'rgba(0,0,0,0.3)',
  height: '100%',
  '&::after': {
    pointerEvents: 'none',
    zIndex: 2,
    position: 'absolute',
    display: 'block',
    content: '',
    top: '3px',
    right: '3px',
    bottom: '3px',
    left: '3px',
    borderRadius: '8px',
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: '2px',
    transition: 'all 300ms ease'
  },
  variants: {
    selected: {
      true: {
        '&::after': {
          borderColor: '#585FFC',
          top: '-1px',
          right: '-1px',
          bottom: '-1px',
          left: '-1px',
          background: 'transparent',
          backdropFilter: 'saturate(150%)'
        }
      }
    },
    semiSelected: {
      true: {
        '&::after': {
          borderColor: '#585FFC',
          opacity: 0.5,
          top: '-1px',
          right: '-1px',
          bottom: '-1px',
          left: '-1px'
        }
      }
    }
  }
})

const Dropdown = () => {
  const loopData = useContext(LoopContext)
  return (
    <>
      {(loopData.showLoopDropdown || loopData.isHoveringDropdown) && (
        <Portal.Root>
          <Box
            onMouseEnter={() => loopData.setIsHoveringDropdown(true)}
            onMouseLeave={() => {
              loopData.setShowLoopDropdown(false)
              loopData.setIsHoveringDropdown(false)
            }}
            onBlur={() => loopData.setShowLoopDropdown(false)}
            ref={loopData.setTooltip}
            style={{ ...loopData.styles.popper, zIndex: '5' }}
            {...loopData.attributes.popper}
          >
            <SelectLoopDropdown
              stemIndex={loopData.stemIndex}
              section={loopData.section}
              loopColor={loopData.loopColor}
              close={() => {
                loopData.setShowLoopDropdown(false)
                loopData.setIsHoveringDropdown(false)
              }}
            />
          </Box>
        </Portal.Root>
      )}
    </>
  )
}

interface LoopBodyProps {
  style: {}
  onClick: () => void
}
const LoopBody = observer(
  React.forwardRef(({ style, onClick }: LoopBodyProps, ref: MutableRefObject<HTMLDivElement>) => {
    const loopData = useContext(LoopContext)
    const { studio } = useTreeState()
    return (
      <LoopContainer
        css={{
          backgroundColor: loopData.loopColor,
          lineHeight: loopData.letterHeight,
          height: '100%',
          ...style
        }}
        semiSelected={loopData.isSelected}
        selected={loopData.isExactSelected}
        onClick={onClick}
        onMouseEnter={() => loopData.setIsHover(true)}
        onMouseLeave={() => {
          loopData.setIsHover(false)
          if (!loopData.isHoveringDropdown) loopData.setShowLoopDropdown(false)
        }}
        ref={ref}
      >
        <Row
          style={{
            fontSize: '14px',
            padding: '0px 4px',
            height: loopData.letterHeight
          }}
          verticalCenter
          justifySpaceBetween
        >
          <Box
            onClick={(e) => {
              e.stopPropagation()
              loopData.setShowLoopDropdown(!loopData.showLoopDropdown)
            }}
            ref={loopData.setTooltipBase}
            css={{ zIndex: '3', cursor: 'pointer' }}
          >
            {LOOP_LETTERS[studio.song.getLoopIndex(loopData.stemIndex, loopData.section) - 1]}
            {loopData.isHover && (
              <Image
                src="/static/icons/expand_more_black_24dp.svg"
                style={{ height: '14px', width: '14px', opacity: 0.3 }}
              />
            )}
          </Box>
          {loopData.isHover && (
            <Image
              onClick={(e) => {
                e.stopPropagation()
                loopData.removeLoop()
              }}
              className="unselectable"
              src="/static/icons/close_black_24dp.svg"
              css={{
                height: '14px',
                width: '14px',
                zIndex: '3',
                opacity: 0.3,
                cursor: 'pointer'
              }}
            />
          )}
        </Row>
        <LoopNoteVisualizer
          stemIndex={loopData.stemIndex}
          section={loopData.section}
          style={{
            width: `100%`,
            height: `calc(${loopData.trackHeight} - ${loopData.letterHeight} - 3px)`,
            padding: '4px 2px'
          }}
          instrumentLoopWidthPX={loopData.instrumentLoopWidthPX}
        />
      </LoopContainer>
    )
  })
)

interface Props {
  loopNumber: number
  updateSong: (songIndex: number) => void
  loopColor: string
  stemIndex: number
  section: number
  trackHeight: string
  onClick: () => void
  instrumentLoopWidthPX: number
  style?: CSSProperties
}

const InstrumentLoop: FC<Props> = ({
  loopNumber,
  updateSong,
  loopColor,
  stemIndex,
  section,
  trackHeight,
  onClick,
  instrumentLoopWidthPX,
  style
}) => {
  const loopData = useInstrumentLoop(
    stemIndex,
    section,
    loopNumber,
    instrumentLoopWidthPX,
    updateSong
  )

  return (
    <LoopContext.Provider
      value={{
        trackHeight,
        loopColor,
        stemIndex,
        section,
        loopNumber,
        instrumentLoopWidthPX,
        ...loopData
      }}
    >
      <ContextMenu.Root>
        <Container
          ref={loopData.connectDrop}
          even={section % 2 === 0}
          css={loopData.instrumentLoopStyling}
        >
          <ContextMenu.Trigger>
            {loopData.currLoop === null && (
              <EmptyInstrumentLoop ref={loopData.setTooltipBase} isEven={true} />
            )}
            {loopData.currLoop !== null && (
              <LoopBody style={style} ref={loopData.drag} onClick={onClick} />
            )}
          </ContextMenu.Trigger>

          <Dropdown />
        </Container>

        <LoopContextMenu />
      </ContextMenu.Root>
    </LoopContext.Provider>
  )
}

export default observer(InstrumentLoop)
